import { useState, useEffect, useCallback } from 'react'

import type {
  AIInterviewPlanTemplate,
  AIInterviewPlanQuestion,
  AIInterviewType,
} from '@ispot/shared'

import {
  usePlan,
  useUpdatePlan,
  useActivatePlan,
  useDeactivatePlan,
  useRegenerateLink,
} from '@/hooks/useAIInterviewPlans'
import { useAIStore } from '@/stores/aiStore'
import { aiInterviewPlanApi, type AIInterviewStreamEvent } from '@/lib/api/client'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  ArrowLeft, Save, Sparkles, Share2, Play, Pause,
  Plus, Copy, RefreshCw, Loader2,
} from 'lucide-react'

import { QuestionCard } from './QuestionCard'
import { PlanMetadataForm } from './PlanMetadataForm'
import { PlanResponsesTab } from './PlanResponsesTab'

interface PlanEditorPageProps {
  accountId: string
  projectId: string
  planId: string
  onBack: () => void
}

type FormState = {
  title: string
  researchGoal: string
  interviewType: AIInterviewType
  focusTopics: string
  description: string
  welcomeMessage: string
  consentText: string
  collectName: boolean
  collectEmail: boolean
  estimatedDuration: string
  maxResponses: string
  questions: AIInterviewPlanQuestion[]
}

function initForm(plan: AIInterviewPlanTemplate): FormState {
  return {
    title: plan.title,
    researchGoal: plan.researchGoal,
    interviewType: plan.interviewType,
    focusTopics: plan.focusTopics.join(', '),
    description: plan.description ?? '',
    welcomeMessage: plan.welcomeMessage ?? '',
    consentText: plan.consentText ?? '',
    collectName: plan.collectName,
    collectEmail: plan.collectEmail,
    estimatedDuration: plan.estimatedDuration?.toString() ?? '',
    maxResponses: plan.maxResponses?.toString() ?? '',
    questions: plan.questions ?? [],
  }
}

export function PlanEditorPage({ accountId, projectId, planId, onBack }: PlanEditorPageProps) {
  const { data: plan, isLoading } = usePlan(accountId, projectId, planId)
  const updatePlan = useUpdatePlan(accountId, projectId)
  const activatePlan = useActivatePlan(accountId, projectId)
  const deactivatePlan = useDeactivatePlan(accountId, projectId)
  const regenerateLink = useRegenerateLink(accountId, projectId)
  const apiKey = useAIStore((s) => s.apiKey)

  const [form, setForm] = useState<FormState | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  useEffect(() => {
    if (plan && !form) setForm(initForm(plan))
  }, [plan, form])

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
    },
    [],
  )

  const handleSave = useCallback(() => {
    if (!form) return
    const topics = form.focusTopics.split(',').map((t) => t.trim()).filter(Boolean)
    updatePlan.mutate({
      planId,
      data: {
        title: form.title,
        researchGoal: form.researchGoal,
        interviewType: form.interviewType,
        focusTopics: topics,
        description: form.description || undefined,
        welcomeMessage: form.welcomeMessage || undefined,
        consentText: form.consentText || undefined,
        collectName: form.collectName,
        collectEmail: form.collectEmail,
        estimatedDuration: form.estimatedDuration ? Number(form.estimatedDuration) : undefined,
        maxResponses: form.maxResponses ? Number(form.maxResponses) : null,
        questions: form.questions,
      },
    })
  }, [form, planId, updatePlan])

  const handleGenerate = useCallback(async () => {
    if (!apiKey) return
    setGenerating(true)
    setGenerateError(null)
    try {
      await aiInterviewPlanApi.generateQuestions(
        accountId, projectId, planId, { apiKey },
        (event: AIInterviewStreamEvent) => {
          if (event.type === 'plan') {
            const plan = (event as { plan?: { questions?: AIInterviewPlanQuestion[] } }).plan
            if (plan?.questions) setField('questions', plan.questions)
          } else if (event.type === 'error') {
            const msg = (event as { message?: string }).message || 'Generation failed'
            setGenerateError(msg)
          }
        },
      )
    } finally {
      setGenerating(false)
    }
  }, [accountId, projectId, planId, apiKey, setField])

  const updateQuestion = (idx: number, patch: Partial<AIInterviewPlanQuestion>) => {
    setForm((prev) => {
      if (!prev) return prev
      const questions = prev.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q))
      return { ...prev, questions }
    })
  }

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    setForm((prev) => {
      if (!prev) return prev
      const target = idx + dir
      if (target < 0 || target >= prev.questions.length) return prev
      const questions = [...prev.questions]
      ;[questions[idx], questions[target]] = [questions[target], questions[idx]]
      return { ...prev, questions }
    })
  }

  const removeQuestion = (idx: number) => {
    setForm((prev) => (prev ? { ...prev, questions: prev.questions.filter((_, i) => i !== idx) } : prev))
  }

  const addQuestion = () => {
    const q: AIInterviewPlanQuestion = { id: crypto.randomUUID(), text: '', type: 'open' }
    setForm((prev) => (prev ? { ...prev, questions: [...prev.questions, q] } : prev))
  }

  const shareUrl = plan ? `${window.location.origin}/interview/${plan.shareToken}` : ''

  if (isLoading || !form || !plan) {
    return (
      <div className="flex h-64 items-center justify-center text-fg-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-accent-500" /> Loading plan...
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-bg">
      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-border bg-bg-card px-4 py-3">
        <button onClick={onBack} className="rounded-md p-1 text-fg-muted hover:bg-bg-secondary hover:text-fg" aria-label="Back to plans">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Input
          value={form.title}
          onChange={(e) => setField('title', e.target.value)}
          className="h-auto border-none bg-transparent p-0 font-display text-heading font-semibold text-fg focus-visible:ring-0"
        />
        <Badge className={plan.status === 'active' ? 'bg-success-bg text-success-fg' : 'bg-warning-bg text-warning-fg'}>{plan.status}</Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating || !apiKey} className="border-border">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Generate Questions
          </Button>
          {plan.status === 'active' ? (
            <Button size="sm" variant="outline" onClick={() => deactivatePlan.mutate(planId)} className="border-border">
              <Pause className="w-4 h-4 mr-1" /> Deactivate
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => activatePlan.mutate(planId)} className="border-border">
              <Play className="w-4 h-4 mr-1" /> Activate
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} className="border-border">
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updatePlan.isPending} className="bg-accent-500 text-bg-card hover:bg-accent-600">
            {updatePlan.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Tabs: Setup + Responses */}
      <Tabs defaultValue="setup" className="flex flex-col flex-1 overflow-hidden">
        <TabsList className="h-auto justify-start rounded-none border-b border-border bg-bg px-4 pt-2">
          <TabsTrigger value="setup" className="rounded-md data-[state=active]:bg-bg-card data-[state=active]:shadow-none focus-visible:ring-accent-500">Setup</TabsTrigger>
          <TabsTrigger value="responses" className="rounded-md data-[state=active]:bg-bg-card data-[state=active]:shadow-none focus-visible:ring-accent-500">
            Responses {plan.sessionCount ? `(${plan.sessionCount})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="flex flex-1 overflow-hidden mt-0">
          <PlanMetadataForm values={form} onChange={setField} />
          <div className="w-2/5 overflow-y-auto p-5 max-lg:w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-fg">Questions ({form.questions.length})</h3>
              <Button size="sm" variant="outline" onClick={handleGenerate} disabled={generating || !apiKey} className="border-border">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Generate with AI
              </Button>
            </div>
            {generateError && (
              <p className="mb-2 text-caption text-danger-fg">{generateError}</p>
            )}
            <div className="space-y-3">
              {form.questions.map((q, idx) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={idx}
                  total={form.questions.length}
                  onUpdate={(patch) => updateQuestion(idx, patch)}
                  onMove={(dir) => moveQuestion(idx, dir)}
                  onRemove={() => removeQuestion(idx)}
                />
              ))}
            </div>
            <Button variant="outline" className="mt-3 w-full border-border" onClick={addQuestion}>
              <Plus className="w-4 h-4 mr-1" /> Add Question
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="responses" className="flex-1 overflow-hidden mt-0">
          <PlanResponsesTab accountId={accountId} projectId={projectId} planId={planId} />
        </TabsContent>
      </Tabs>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Share Interview Link</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 mt-2">
            <Input readOnly value={shareUrl} className="text-sm" />
            <Button
              size="sm" variant="outline"
              onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
            >
              <Copy className="w-4 h-4 mr-1" /> {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="mt-2" onClick={() => regenerateLink.mutate(planId)} disabled={regenerateLink.isPending}>
            <RefreshCw className="w-4 h-4 mr-1" /> Regenerate Link
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
